/**
 * Les indemnités kilométriques : le barème fiscal, et ce qu'il permet selon
 * le statut de celui qui roule.
 *
 * LE BARÈME N'A PAS BOUGÉ DEPUIS 2023. Celui de l'arrêté du 27 mars 2023
 * (JO du 7 avril 2023, article 6 B de l'annexe IV du CGI) vaut pour les
 * revenus 2022 à 2025 : service-public l'a rappelé le 9 avril 2026 (« comme
 * en 2025, les barèmes kilométriques ne sont pas revalorisés »). Celui des
 * revenus 2026 n'est pas publié ; `revueAvantLe` oblige à le relire.
 *
 * UNE FORMULE PAR TRANCHE, APPLIQUÉE À TOUTE LA DISTANCE. La tranche se
 * choisit sur les kilomètres professionnels de l'année, puis sa formule
 * s'applique à tous : ce n'est pas un barème marginal. La courbe est continue
 * à 5 000 km et saute à 20 000 km, et c'est voulu par le texte.
 *
 * L'ÉLECTRIQUE SUIT LA TABLE PUBLIÉE. Le texte dit « majoré de 20 % » ; la
 * brochure de l'impôt sur le revenu, l'Urssaf et le simulateur officiel
 * publient des coefficients arrondis au millième et des montants fixes à
 * l'euro. Nous suivons la table publiée, comme le simulateur officiel : l'écart
 * avec « thermique × 1,2 » ne dépasse pas quelques euros.
 *
 * TOUT SE CALCULE EN MILLIÈMES ENTIERS. En virgule flottante, 5 000 × 0,697
 * donne 3 484,999… : les coefficients sont donc stockés en millièmes, et le
 * montant arrondi au centime une seule fois.
 *
 * QUI PEUT S'EN SERVIR (relevé du 24 septembre 2026, voir la fiche) :
 *   - micro-entrepreneur : personne, l'abattement couvre déjà les frais de
 *     déplacement ;
 *   - entrepreneur individuel en BIC : le barème n'est pas admis, seuls les
 *     frais réels ou le barème carburant le sont (BOFiP, BNC § 580) ;
 *   - entrepreneur individuel en BNC : option annuelle, pour tous ses
 *     véhicules ;
 *   - président de SASU, gérant d'EURL à l'IS : la société rembourse, et des
 *     kilomètres exacts et justifiés en font des frais réels, non imposables.
 */

/** Les coefficients en millièmes d'euro par kilomètre, et les montants fixes en euros. */
export const BAREME_IK = {
    voiture: {
        seuils: [5000, 20000],
        puissances: ["3", "4", "5", "6", "7"],
        thermique: {
            3: [[529, 0], [316, 1065], [370, 0]],
            4: [[606, 0], [340, 1330], [407, 0]],
            5: [[636, 0], [357, 1395], [427, 0]],
            6: [[665, 0], [374, 1457], [447, 0]],
            7: [[697, 0], [394, 1515], [470, 0]],
        },
        electrique: {
            3: [[635, 0], [379, 1278], [444, 0]],
            4: [[727, 0], [408, 1596], [488, 0]],
            5: [[763, 0], [428, 1674], [512, 0]],
            6: [[798, 0], [449, 1748], [536, 0]],
            7: [[836, 0], [473, 1818], [564, 0]],
        },
    },
    moto: {
        seuils: [3000, 6000],
        puissances: ["1-2", "3-5", "5+"],
        thermique: {
            "1-2": [[395, 0], [99, 891], [248, 0]],
            "3-5": [[468, 0], [82, 1158], [275, 0]],
            "5+": [[606, 0], [79, 1583], [343, 0]],
        },
        electrique: {
            "1-2": [[474, 0], [119, 1069], [298, 0]],
            "3-5": [[562, 0], [98, 1390], [330, 0]],
            "5+": [[727, 0], [95, 1900], [412, 0]],
        },
    },
    cyclomoteur: {
        seuils: [3000, 6000],
        puissances: ["unique"],
        thermique: { unique: [[315, 0], [79, 711], [198, 0]] },
        electrique: { unique: [[378, 0], [95, 853], [238, 0]] },
    },
};

export const VEHICULES = Object.keys(BAREME_IK);

/** Les statuts, et ce que chacun peut faire du barème. */
export const STATUTS_IK = {
    micro: "refus",
    bic: "refus",
    bnc: "deduction",
    eurlIr: "inconnu",
    eurl: "remboursement",
    sasu: "remboursement",
};

/**
 * Les indemnités d'une année pour un véhicule.
 *
 * @param vehicule    « voiture », « moto » ou « cyclomoteur »
 * @param puissance   voiture : « 3 » à « 7 » (7 vaut 7 CV et plus, 3 vaut
 *                    3 CV et moins) ; moto : « 1-2 », « 3-5 », « 5+ »
 * @param electrique  100 % électrique ; les hybrides suivent le thermique
 * @param km          kilomètres professionnels de l'année
 */
export function indemnites({ vehicule, puissance, electrique = false, km }) {
    const bareme = BAREME_IK[vehicule];
    if (!bareme) return null;
    const cle = vehicule === "cyclomoteur" ? "unique" : String(puissance);
    const tranches = bareme[electrique ? "electrique" : "thermique"][cle];
    if (!tranches) return null;
    // Le simulateur officiel tronque la distance à l'entier ; nous aussi.
    const d = Math.max(0, Math.floor(Number(km) || 0));
    const rang = d <= bareme.seuils[0] ? 0 : d <= bareme.seuils[1] ? 1 : 2;
    const [coefficient, fixe] = tranches[rang];
    const montant = Math.round((d * coefficient) / 10) / 100 + fixe;
    return {
        km: d,
        tranche: rang,
        coefficient: coefficient / 1000,
        fixe,
        montant,
        parKm: d > 0 ? montant / d : 0,
    };
}
